import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { useGetAllBusinessPlans } from 'services/businessPlan/hooks';
import { useCheckoutSubscription } from 'services/payments/subscriptions/hooks';
import { UsersApi } from 'services/users/users.api';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessToolStoreClear } from 'store/useBusinessToolStore/selectors';
import { useUserLimits } from 'store/useUserLimits';
import { getClearLimits } from 'store/useUserLimits/selectors';
import { useUserStore } from 'store/useUserStore';
import { getSetAuthUser } from 'store/useUserStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getSetUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import useCreateBusinessPlanHandler from 'pages/Workspace/hooks/useCreateBusinessPlanHandler';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { PageLoader } from 'components/_basic/PageLoader/PageLoader';

import { getLoginType, listenAuthorizationEvents, userLogOut } from 'helpers/authentication.utils';
import { getVariable } from 'helpers/environment.utils';
import { useInactivityHandler } from 'helpers/inactivityHandler/useInactivityHandler';
import ToastService from 'helpers/ToastService';

import { IAuthProviderProps } from './types';
import { SaveDocumentOnLogin } from '../SaveDocumentOnLogin/SaveDocumentOnLogin';

export const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {
  const setAuthUser = useUserStore(getSetAuthUser);
  const { isAuthenticated } = useAuthenticated();
  const { data: businessPlansData } = useGetAllBusinessPlans();

  const setUserSubscription = useUserSubscriptionStore(getSetUserSubscription);
  const businessToolStoreClear = useBusinessToolStore(getBusinessToolStoreClear);
  const clearLimits = useUserLimits(getClearLimits);

  const checkoutMutation = useCheckoutSubscription();
  const { t } = useTranslation();

  const { createBusinessPlanHandler } = useCreateBusinessPlanHandler({
    type: BUSINESS_PLAN_TYPES.FOR_BANK,
    isPlanAvailable: true,
  });

  const [isUserLoading, setIsUserLoading] = useState(false);
  // const [isDocLimitComponentShown, setIsDocLimitComponentShown] = useState(false);

  useInactivityHandler(isAuthenticated);

  useEffect(() => {
    const authenticateAndSetUser = async () => {
      try {
        setIsUserLoading(true);
        const { tokens, userSub } = await fetchAuthSession();

        if (!tokens) {
          return;
        }

        const username = tokens.accessToken.payload.username?.toString();
        const email = tokens.idToken?.payload.email?.toString();
        const emailVerified = Boolean(tokens.idToken?.payload.email_verified);
        const loginType = getLoginType(username);
        const firstName = tokens.idToken?.payload.name?.toString();
        const lastName = tokens.idToken?.payload.family_name?.toString();

        if (!loginType) {
          userLogOut();
          ToastService.showError('Log in type not found');
          return;
        }

        let isSocialMediaSignUpFlow = false;
        if (username && email) {
          const registeredData = await UsersApi.verifyEmail({ email });
          const isRegistered = Object.values(registeredData.registeredAccounts).includes(true);

          if (!isRegistered) {
            await UsersApi.register({
              email,
              loginType,
              username,
              emailVerified,
              preferredLanguage: 'EN',
              userPoolId: getVariable('REACT_APP_ENV_AWS_USER_POOL_ID'),
            });
            const user = await UsersApi.login({ loginType, email: email ?? '' });
            const userDetails = {
              firstName: firstName ?? '',
              lastName: lastName ?? '',
            };
            await UsersApi.putUserDetails(userDetails);
            setAuthUser({ ...user, ...userDetails, loginType, id: userSub ?? '' });

            if (localStorage.getItem('choosedSubscription')) {
              try {
                const payload = JSON.parse(localStorage.getItem('choosedSubscription')!);

                const response = await checkoutMutation.mutateAsync(payload);

                if (response.url) {
                  window.location.href = response.url;
                } else {
                  ToastService.showError(t('noNavigationInformation'));
                }
              } catch (error) {
                if (error instanceof Error) {
                  ToastService.showError(error.message);
                }
              }
            } else if (
              businessPlansData &&
              !businessPlansData?.content?.find((item) => item.type === 'BANK' || item.type === 'LIGHT')
            ) {
              try {
                await createBusinessPlanHandler();
              } catch (error) {
                if (error instanceof Error) {
                  ToastService.showError(error.message);
                }
              }
            }
            isSocialMediaSignUpFlow = true;
          }
        }

        if (!isSocialMediaSignUpFlow) {
          const user = await UsersApi.login({ loginType, email: email ?? '' });

          const userDetails = await UsersApi.getUserDetails();
          setAuthUser({ ...user, ...userDetails, loginType, id: userSub ?? '' });
        }
      } catch (error) {
        if (error instanceof Error) {
          userLogOut();
          ToastService.showError(error.message);
        }
      } finally {
        setIsUserLoading(false);
      }
    };

    const authClear = () => {
      setAuthUser(null);
      setUserSubscription(null);
      clearLimits();
      businessToolStoreClear();
      // setIsDocLimitComponentShown(false);
    };

    listenAuthorizationEvents('signedOut', () => {
      authClear();
    });

    listenAuthorizationEvents('tokenRefresh_failure', () => {
      signOut({ global: true });
      authClear();
    });

    listenAuthorizationEvents('signedIn', () => {
      authenticateAndSetUser();
      // setIsDocLimitComponentShown(true);
    });
  }, []);

  if (isUserLoading) {
    return <PageLoader />;
  }
  return (
    <>
    {/*TODO temporary remove document exceed limit logic*/}
      {/* {isDocLimitComponentShown && <SaveDocumentOnLogin />} */}
      {children}
    </>
  );
};
